import React from "react";
import { Dialog, Box, IconButton, useMediaQuery } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronLeft, faTimes } from "@fortawesome/free-solid-svg-icons";
import { VeBox, VeTypography } from "../../mui";
import { VeButton } from "..";
import "./modal.scss";

interface ConfirmationModalProps {
  children?: React.ReactNode;
  title?: string;
  goBack?: boolean;
  styles?: React.CSSProperties;
  fixWidth?: boolean;
  desc?: string | React.ReactNode;
  handleGoBack?: () => void;
  outlinedButtonTitle?: string;
  disableContainedButton?: boolean;
  containedButtonTitle?: string;
  containedOnClick?: () => void;
  outlinedOnClick?: () => void;
  isPending?: boolean;
  width?: string;
  open: boolean;
  onClose?: any;
  iconSvg?: any;
  icon?: any;
  titleStart?: boolean;
  fixedWidth?: boolean;
  deleting?: boolean;
  deletingSecondary?: boolean;
  outlinedIsPending?: boolean;
  disbaleOutlinedButton?: boolean;
  paddingTopForChildren?: boolean;
  titleWidth?: string;
  belowText?: string;
  customHandler?: any;
  descNewLine?: boolean;
}

const useStyles = makeStyles({
  hover: {
    "&:hover": { background: "#f3f3f3", borderRadius: "100%" },
  },
  modal: {
    "& .MuiPaper-rounded": {
      borderRadius: "8px !important",
    },
  },
  emailTag: {
    display: "inline-block",
    backgroundColor: "#F3F4F6",
    padding: "4px 12px",
    borderRadius: "6px",
  },
});

export function ConfirmationModal({
  children,
  title,
  goBack,
  desc,
  handleGoBack,
  outlinedButtonTitle,
  disableContainedButton,
  containedButtonTitle,
  containedOnClick,
  outlinedOnClick,
  width = "436px",
  isPending,
  open,
  onClose,
  styles,
  iconSvg,
  icon,
  titleStart = false,
  fixedWidth = false,
  deleting = false,
  deletingSecondary = false,
  outlinedIsPending = false,
  disbaleOutlinedButton = false,
  paddingTopForChildren = false,
  titleWidth = "100%",
  belowText = "",
  customHandler,
  descNewLine = false,
}: ConfirmationModalProps) {
  const classes = useStyles();
  const isMobile = useMediaQuery("(max-width:600px)");

  // Function to render description with email tag or React elements
  const renderDescription = () => {
    if (!desc) return null;

    // If desc is a React element/node, render it directly
    if (typeof desc !== "string") {
      return (
        <Box
          display="flex"
          justifyContent="center"
          paddingBottom="1.5rem"
          sx={{ alignItems: "center", flexWrap: "wrap" }}
        >
          {desc}
        </Box>
      );
    }

    // Original string logic with email tag support
    const targetEmail = "support@vericap.io";
    const parts = desc.split(targetEmail);

    return (
      <Box
        display="flex"
        justifyContent="center"
        paddingBottom="1.5rem"
        sx={{ alignItems: "center", flexWrap: "wrap" }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            justifyContent: "center",
          }}
        >
          {parts.map((part, index) => (
            <React.Fragment key={index}>
              <VeTypography
                variant="body2Disabled"
                align="center"
                p={0}
                component="span"
                sx={{ whiteSpace: descNewLine ? "pre-line" : "none" }}
              >
                {part}
              </VeTypography>
              {index < parts.length - 1 && (
                <VeTypography
                  variant="body2Brand"
                  component="span"
                  // className={classes.emailTag}
                  sx={{ display: "inline-block" }}
                  onClick={() => {
                    customHandler && customHandler();
                    onClose();
                  }}
                >
                  {targetEmail}
                </VeTypography>
              )}
            </React.Fragment>
          ))}
        </Box>
      </Box>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      className={classes.modal}
      aria-labelledby="responsive-dialog-title"
      disableScrollLock
      hideBackdrop={false}
      PaperProps={{
        sx: {
          zIndex: "1301 !important", // Ensure modal appears above drawer (drawer z-index is 1200)
        },
      }}
    >
      <Box
        maxWidth={isMobile ? "100%" : width}
        width={width ? (isMobile ? width : "100%") : "auto"}
        sx={{ padding: "16px 16px 24px 16px" }}
      >
        {iconSvg && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              paddingBottom: "16px",
            }}
          >
            <Box
              onClick={onClose}
              sx={{
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
              }}
            >
              <CloseIcon />
            </Box>
          </Box>
        )}

        {(icon || iconSvg) && (
          <Box marginBottom={"16px"}>
            {iconSvg && (
              <Box
                display="flex"
                justifyContent="center"
                sx={{ alignItems: "center" }}
              >
                <Box sx={{ textAlign: "center" }}>{iconSvg}</Box>
              </Box>
            )}
            {icon && <FontAwesomeIcon width="22px" height="22px" icon={icon} />}
          </Box>
        )}
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            paddingBottom: "16px",
          }}
        >
          <Box
            display="flex"
            justifyContent="center"
            sx={{ alignItems: "center" }}
            marginLeft={titleStart ? "start" : "auto"}
            marginRight={"auto"}
            // pb="1.5rem"
          >
            {title && (
              <VeTypography
                variant="subtitle2StrongerMain"
                align="center"
                p={0}
                width={titleWidth ? titleWidth : "100%"}
              >
                {title}
              </VeTypography>
            )}
          </Box>
          {onClose && !iconSvg && (
            <Box
              onClick={onClose}
              sx={{
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
              }}
            >
              <CloseIcon />
            </Box>
          )}
        </Box>
        <Box className="modal-div-gif">
          <Box sx={{ width: "93%", margin: "0 auto"}}>
            {renderDescription()}
            <Box>
              {children && (
                <Box
                  paddingBottom="1.5rem"
                  paddingTop={paddingTopForChildren ? "0px" : "24px"}
                  display="flex"
                  justifyContent="center"
                  sx={{ lineHeight: "0" }}
                >
                  <Box width="436px">{children}</Box>
                </Box>
              )}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  gap: "16px",
                }}
              >
                {outlinedButtonTitle && (
                  <VeBox
                    sx={{
                      width: isMobile ? "47%" : "auto",
                    }}
                  >
                    <VeButton
                      buttonVariant={
                        deletingSecondary ? "contained" : "outlined"
                      }
                      color={deletingSecondary ? "error" : "primary"}
                      size="medium"
                      buttonFullWidth={isMobile}
                      buttonClick={outlinedOnClick}
                      styles={styles}
                      buttonMinWidth={"96px"}
                      loading={outlinedIsPending}
                      disabled={disbaleOutlinedButton}
                    >
                      {outlinedButtonTitle}
                    </VeButton>
                  </VeBox>
                )}
                {containedButtonTitle && (
                  <VeBox
                    sx={{
                      width: isMobile ? "47%" : "auto",
                    }}
                  >
                    <VeButton
                      buttonVariant="contained"
                      color={deleting ? "error" : "primary"}
                      loading={isPending}
                      buttonFullWidth={isMobile}
                      size="medium"
                      buttonClick={containedOnClick}
                      disabled={disableContainedButton}
                      buttonMinWidth={"96px"}
                    >
                      {containedButtonTitle}
                    </VeButton>
                  </VeBox>
                )}
              </Box>
              {belowText?.length > 0 && (
                <VeBox
                  sx={{
                    width: isMobile ? "100%" : "auto",
                    display: "flex",
                    justifyContent: "center",
                    marginTop: "16px",
                  }}
                >
                  <VeTypography variant="label3Tertiary" align="center" p={0}>
                    {belowText}
                  </VeTypography>
                </VeBox>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
}

const CloseIcon = () => {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 14 14"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M13.295 2.115C13.6844 1.72564 13.6844 1.09436 13.295 0.705C12.9056 0.315639 12.2744 0.315639 11.885 0.705L7 5.59L2.115 0.705C1.72564 0.315639 1.09436 0.315639 0.705 0.705C0.315639 1.09436 0.315639 1.72564 0.705 2.115L5.59 7L0.705 11.885C0.315639 12.2744 0.315639 12.9056 0.705 13.295C1.09436 13.6844 1.72564 13.6844 2.115 13.295L7 8.41L11.885 13.295C12.2744 13.6844 12.9056 13.6844 13.295 13.295C13.6844 12.9056 13.6844 12.2744 13.295 11.885L8.41 7L13.295 2.115Z"
        fill="#5F6368"
        width="24"
        height="24"
      />
    </svg>
  );
};
